import type { ReactElement } from "react";
import type { Zahlstatus } from "@kundeninfo/api-client";
import { Chip } from "@kundeninfo/ui/me-design";
import type { ObjektStatusArt } from "@kundeninfo/ui/me-design";

/**
 * DAS ZAHLSTATUS-KENNZEICHEN der Lieferverträge (Strang C) und Eingangsrechnungen (Strang D) — Spec „Zahlstatus“.
 * Ein Kennzeichen (Chip ohne Rahmen, Fläche nach Stufe), bei „angenommen bezahlt“ mit dem leisen Zusatz „angenommen“
 * („OK mit Zusatz angenommen“). Den Zustand rechnet der Server (`paymentState` der Domäne); hier wird nur gezeigt.
 *
 * Die Lesegestalt ist der Vertragstyp `Zahlstatus` aus `@kundeninfo/api-client` (Aufgabe 22; bis dahin ein lokaler
 * Spiegel, weil `web` die Domäne nie importiert).
 */
export type PaymentState = Zahlstatus["state"];

export const PAYMENT_STATE_TEXT: Record<PaymentState, string> = {
  OVERDUE: "überfällig", DUE_SOON: "fällig", PART_PAID: "teilbezahlt", CREDIT_OPEN: "Guthaben offen", OPEN: "offen",
  ASSUMED_PAID: "bezahlt", PAID: "bezahlt", CANCELLED: "storniert",
};

const tage = (n: number): string => (n === 1 ? "1 Tag" : `${n} Tagen`);
export const PAYMENT_TEXT = {
  assumed: "angenommen",
  dueToday: "fällig heute",
  dueIn: (n: number) => `fällig in ${tage(n)}`,
  overdueSince: (n: number) => `überfällig seit ${tage(n)}`,
};

/** „fällig in 3 Tagen“, „fällig heute“, „überfällig seit 2 Tagen“, sonst der Tabellentext. */
export function paymentLabel(v: { state: PaymentState; daysLeft: number | null }): string {
  if (v.daysLeft !== null && v.state === "DUE_SOON") {
    return v.daysLeft === 0 ? PAYMENT_TEXT.dueToday : PAYMENT_TEXT.dueIn(v.daysLeft);
  }
  if (v.daysLeft !== null && v.daysLeft < 0 && v.state === "OVERDUE") return PAYMENT_TEXT.overdueSince(-v.daysLeft);
  return PAYMENT_STATE_TEXT[v.state];
}

const STATUS_ART = { ERROR: "fehler", WARN: "warnung", OK: "ok" } as const;

/** Der Zahlstatus als Text und Stufe (PF11) — EINE Abbildung für das Kennzeichen und den Status am `Objekt`. */
export function paymentStatus(payment: Zahlstatus): { text: string; art: ObjektStatusArt } {
  return { text: paymentLabel(payment), art: payment.level === null ? "aus" : STATUS_ART[payment.level] };
}

export function ZahlstatusKennzeichen({ payment }: { payment: Zahlstatus }): ReactElement {
  const s = paymentStatus(payment);
  return (
    <span>
      <Chip statusArt={s.art}>{s.text}</Chip>
      {payment.state === "ASSUMED_PAID" && <Chip statusArt="aus">{PAYMENT_TEXT.assumed}</Chip>}
    </span>
  );
}
